import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Download, Smartphone, AlertCircle, Copy, ExternalLink, ArrowRight, RefreshCw, ChevronDown, ChevronUp, Settings, Search, CheckCircle } from 'lucide-react'
import { ENV_CONFIG } from '../config/env'

const DownloadSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'register' | 'temp'>('register')
  const [expandedStep, setExpandedStep] = useState<number | null>(null)
  const [tempAccounts, setTempAccounts] = useState<Array<{ appleid: string, password: string }>>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasLoaded, setHasLoaded] = useState(false) // 新增：标记是否已加载过数据

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
    // 可以添加一个临时的成功提示
    const button = event?.target as HTMLElement
    if (button) {
      const originalText = button.innerHTML
      button.innerHTML = '✓'
      button.style.color = '#10b981'
      setTimeout(() => {
        button.innerHTML = originalText
        button.style.color = ''
      }, 1000)
    }
  }

  const fetchTempAccounts = async () => {
    // 如果已经加载过数据，直接返回
    if (hasLoaded && tempAccounts.length > 0) {
      return
    }
    
    setLoading(true)
    setError(null)
    try {
      // 使用环境配置
      const apiUrl = ENV_CONFIG.API.getUrl()
      const requestOptions = ENV_CONFIG.REQUEST.getOptions()
      
      console.log(`🔍 请求API: ${apiUrl}`, ENV_CONFIG.isDevelopment ? '(开发环境)' : '(生产环境)')
      
      const response = await fetch(apiUrl, requestOptions)

      if (!response.ok) {
        if (response.status === 500) {
          throw new Error('服务器繁忙，请稍后重试~')
        } else if (response.status === 504) {
          throw new Error('请求超时，请检查网络连接')
        } else {
          throw new Error(`网络请求失败 (${response.status})`)
        }
      }

      const result = await response.json()

      if (result.code === 1 && result.data) {
        setTempAccounts(result.data)
        setHasLoaded(true) // 标记已加载
      } else {
        throw new Error(result.msg || '获取账号失败')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取临时账号失败')
      console.error('获取临时账号失败:', err)
    } finally {
      setLoading(false)
    }
  }

  // 页面加载时只请求一次API
  useEffect(() => {
    fetchTempAccounts()
  }, []) // 空依赖数组，只在组件挂载时执行一次

  const tutorialSteps = [
    {
      id: 1,
      title: '准备工作',
      description: '确保您的设备满足基本要求',
      icon: Smartphone,
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
      details: [
        'iPhone/iPad 运行 iOS 12.0 或更高版本',
        '稳定的网络连接（WiFi 或 4G/5G）',
        '足够的存储空间（至少 50MB）',
        '确保设备已解锁并可以访问 App Store'
      ]
    },
    {
      id: 2,
      title: '获取外区 Apple ID',
      description: '注册或使用临时账号',
      icon: Settings,
      color: 'text-green-500',
      bgColor: 'bg-green-50',
      details: [
        '访问 Apple ID 注册页面',
        '选择美国、香港或日本等地区',
        '填写邮箱、密码等必要信息',
        '完成邮箱验证和账号激活',
        '或者使用我们提供的临时账号'
      ],
      hasAppleIdSection: true
    },
    {
      id: 3,
      title: '登录 App Store',
      description: '切换到外区账号',
      icon: Search,
      color: 'text-purple-500',
      bgColor: 'bg-purple-50',
      details: [
        '打开 App Store 应用',
        '点击右上角头像或"账户"',
        '选择"退出登录"（如果已登录其他账号）',
        '输入外区 Apple ID 和密码',
        '同意服务条款和隐私政策'
      ]
    },
    {
      id: 4,
      title: '搜索并下载',
      description: '找到并安装暴风加速器',
      icon: Download,
      color: 'text-orange-500',
      bgColor: 'bg-orange-50',
      details: [
        '在 App Store 搜索框中输入"暴风加速器"',
        '或者搜索"NiceVPN"（备用名称）',
        '点击应用图标进入详情页',
        '点击"获取"或"下载"按钮',
        '使用 Face ID、Touch ID 或密码确认下载',
        '等待下载和安装完成'
      ]
    },
    {
      id: 5,
      title: '首次使用',
      description: '配置和连接VPN',
      icon: CheckCircle,
      color: 'text-indigo-500',
      bgColor: 'bg-indigo-50',
      details: [
        '打开暴风加速器应用',
        '阅读并同意用户协议和隐私政策',
        '选择您偏好的服务器位置',
        '点击"连接"按钮开始使用',
        '首次连接可能需要允许VPN配置',
        '连接成功后即可畅游全球网络'
      ]
    }
  ]



  return (
    <section id="download" className="py-20 bg-gray-50">
      <div className="container-responsive">
        {/* 标题区域 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="mobile-heading font-bold text-gray-900 mb-4">
            下载教程
            <span className="text-gradient"> 暴风加速器</span>
          </h2>
          <p className="mobile-text text-gray-600 max-w-3xl mx-auto">
            跟随我们的详细教程，轻松完成下载和配置，开始您的极速VPN体验
          </p>
        </motion.div>

        {/* 详细教程步骤 */}
        <div className="max-w-4xl mx-auto mb-20">
          <div className="space-y-4">
            {tutorialSteps.map((step, index) => (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card cursor-pointer hover:shadow-lg transition-shadow duration-300"
                onClick={() => setExpandedStep(expandedStep === step.id ? null : step.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 ${step.bgColor} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <step.icon className={`w-6 h-6 ${step.color}`} />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-sm font-medium text-gray-500">步骤 {step.id}</span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {step.title}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {step.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    {expandedStep === step.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>

                <AnimatePresence>
                  {expandedStep === step.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-6 pt-6 border-t border-gray-100"
                    >
                      <div className="space-y-6">
                        <div className="space-y-3">
                          {step.details.map((detail, detailIndex) => (
                            <div key={detailIndex} className="flex items-start space-x-3">
                              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                              <p className="text-gray-700">{detail}</p>
                            </div>
                          ))}
                        </div>
                        
                        {/* Apple ID获取区域 */}
                        {step.hasAppleIdSection && (
                          <div className="mt-8 p-6 bg-gray-50 rounded-xl">
                            <h4 className="text-lg font-semibold text-gray-900 mb-4">选择获取方式</h4>
                            
                                                         {/* 选项卡 */}
                             <div className="flex space-x-1 bg-white p-1 rounded-lg mb-4">
                               <button
                                 onClick={(e) => {
                                   e.stopPropagation() // 阻止事件冒泡
                                   setActiveTab('register')
                                 }}
                                 className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all duration-200 ${activeTab === 'register'
                                     ? 'bg-blue-100 text-blue-600'
                                     : 'text-gray-600 hover:text-gray-900'
                                   }`}
                               >
                                 自主注册(*推荐*)
                               </button>
                               <button
                                 onClick={(e) => {
                                   e.stopPropagation() // 阻止事件冒泡
                                   setActiveTab('temp')
                                 }}
                                 className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all duration-200 ${activeTab === 'temp'
                                     ? 'bg-blue-100 text-blue-600'
                                     : 'text-gray-600 hover:text-gray-900'
                                   }`}
                               >
                                 临时账号
                               </button>
                             </div>

                            <AnimatePresence mode="wait">
                              {activeTab === 'register' && (
                                <motion.div
                                  key="register"
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: -10 }}
                                  transition={{ duration: 0.2 }}
                                  className="space-y-4"
                                >
                                  <div className="bg-white p-4 rounded-lg border">
                                    <h5 className="font-semibold text-gray-900 mb-3">自主注册步骤</h5>
                                    <div className="space-y-2">
                                      <div className="flex items-start space-x-2">
                                        <div className="w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5">
                                          1
                                        </div>
                                        <p className="text-sm text-gray-600">访问 Apple ID 注册页面</p>
                                      </div>
                                      <div className="flex items-start space-x-2">
                                        <div className="w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5">
                                          2
                                        </div>
                                        <p className="text-sm text-gray-600">选择美国、香港或日本等地区</p>
                                      </div>
                                      <div className="flex items-start space-x-2">
                                        <div className="w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5">
                                          3
                                        </div>
                                        <p className="text-sm text-gray-600">填写邮箱、密码等信息</p>
                                      </div>
                                      <div className="flex items-start space-x-2">
                                        <div className="w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5">
                                          4
                                        </div>
                                        <p className="text-sm text-gray-600">完成验证并激活账号</p>
                                      </div>
                                    </div>
                                    <a
                                      href="https://mp.weixin.qq.com/s/2ueIa_dyYeQF48L1E1q8vQ"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="w-full mt-4 btn-primary text-sm py-2 px-4 flex items-center justify-center space-x-2"
                                    >
                                      <ExternalLink className="w-4 h-4" />
                                      <span>前往注册</span>
                                    </a>
                                  </div>
                                </motion.div>
                              )}

                              {activeTab === 'temp' && (
                                <motion.div
                                  key="temp"
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: -10 }}
                                  transition={{ duration: 0.2 }}
                                  className="space-y-3"
                                >
                                  {/* 加载状态 */}
                                  {loading && (
                                    <motion.div
                                      initial={{ opacity: 0 }}
                                      animate={{ opacity: 1 }}
                                      className="bg-white p-4 rounded-lg border text-center"
                                    >
                                      <RefreshCw className="w-6 h-6 text-blue-500 animate-spin mx-auto mb-2" />
                                      <p className="text-sm text-gray-600">正在获取临时账号...</p>
                                    </motion.div>
                                  )}

                                  {/* 错误状态 */}
                                  {error && (
                                    <motion.div
                                      initial={{ opacity: 0 }}
                                      animate={{ opacity: 1 }}
                                      className="bg-white p-4 rounded-lg border border-red-200 bg-red-50"
                                    >
                                      <div className="flex items-start space-x-2">
                                        <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                        <div>
                                          <h5 className="font-semibold text-red-800 mb-1 text-sm">获取失败</h5>
                                          <p className="text-xs text-red-700 mb-3">{error}</p>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation() // 阻止事件冒泡
                                              setHasLoaded(false) // 重置加载状态
                                              fetchTempAccounts()
                                            }}
                                            className="btn-primary text-xs py-1 px-3 flex items-center space-x-1"
                                          >
                                            <RefreshCw className="w-3 h-3" />
                                            <span>重试</span>
                                          </button>
                                        </div>
                                      </div>
                                    </motion.div>
                                  )}

                                  {/* 账号列表 */}
                                  {!loading && !error && tempAccounts.length > 0 && (
                                    <>
                                      {tempAccounts.map((account, index) => (
                                        <motion.div
                                          key={account.appleid}
                                          initial={{ opacity: 0, y: 10 }}
                                          animate={{ opacity: 1, y: 0 }}
                                          transition={{ delay: index * 0.05 }}
                                          className="bg-white p-4 rounded-lg border"
                                        >
                                          <div className="flex items-center justify-between mb-3">
                                            <h5 className="font-semibold text-gray-900 text-sm">临时账号 {index + 1}</h5>
                                            <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
                                              美国
                                            </span>
                                          </div>
                                          <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                              <span className="text-xs text-gray-600">邮箱:</span>
                                              <div className="flex items-center space-x-1">
                                                <span className="font-mono text-xs">{account.appleid}</span>
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation() // 阻止事件冒泡
                                                    handleCopy(account.appleid)
                                                  }}
                                                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                                                  title="复制邮箱"
                                                >
                                                  <Copy className="w-3 h-3 text-gray-500" />
                                                </button>
                                              </div>
                                            </div>
                                            <div className="flex items-center justify-between">
                                              <span className="text-xs text-gray-600">密码:</span>
                                              <div className="flex items-center space-x-1">
                                                <span className="font-mono text-xs">******</span>
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation() // 阻止事件冒泡
                                                    handleCopy(account.password)
                                                  }}
                                                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                                                  title="复制密码"
                                                >
                                                  <Copy className="w-3 h-3 text-gray-500" />
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                        </motion.div>
                                      ))}

                                      {/* 刷新按钮 */}
                                      <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 0.3 }}
                                        className="text-center"
                                      >
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation() // 阻止事件冒泡
                                            setHasLoaded(false) // 重置加载状态
                                            fetchTempAccounts()
                                          }}
                                          className="btn-secondary text-xs py-1 px-3 flex items-center space-x-1 mx-auto"
                                        >
                                          <RefreshCw className="w-3 h-3" />
                                          <span>刷新账号列表</span>
                                        </button>
                                      </motion.div>
                                    </>
                                  )}

                                  {/* 空状态 */}
                                  {!loading && !error && tempAccounts.length === 0 && (
                                    <motion.div
                                      initial={{ opacity: 0 }}
                                      animate={{ opacity: 1 }}
                                      className="bg-white p-4 rounded-lg border text-center"
                                    >
                                      <AlertCircle className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                                      <p className="text-sm text-gray-600 mb-3">暂无可用临时账号</p>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation() // 阻止事件冒泡
                                          setHasLoaded(false) // 重置加载状态
                                          fetchTempAccounts()
                                        }}
                                        className="btn-primary text-xs py-1 px-3 flex items-center space-x-1 mx-auto"
                                      >
                                        <RefreshCw className="w-3 h-3" />
                                        <span>重新获取</span>
                                      </button>
                                    </motion.div>
                                  )}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>

        {/* 安全提示 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-xl">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-yellow-800 mb-2">重要提醒</h4>
                <p className="text-sm text-yellow-700 leading-relaxed">
                  临时账号仅供下载使用，请勿在系统设置中登录。下载完成后请使用自己的Apple ID。我们提供的临时账号不会记录任何个人信息，确保您的隐私安全。
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* 底部CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-8 md:p-12 text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              准备好开始了吗？
            </h3>
            <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
              立即下载暴风加速器，体验极速、安全、稳定的VPN服务
            </p>
            <a
              href="https://apps.apple.com/app/id6473819031"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-large bg-white text-blue-600 hover:bg-gray-50 flex items-center justify-center space-x-2 mx-auto max-w-sm"
            >
              <Download className="w-5 h-5" />
              <span>跳转AppStore下载</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </motion.div>

        <div style={{ height: '40px' }}></div>
        
        {/* 常见问题 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-12">
            常见问题
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="card"
            >
              <h4 className="font-semibold text-gray-900 mb-3 flex items-start space-x-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>为什么需要外区 Apple ID？</span>
              </h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                由于国内应用市场政策限制，VPN类应用无法在国区App Store上架，因此需要使用非大陆地区的Apple ID进行下载。
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="card"
            >
              <h4 className="font-semibold text-gray-900 mb-3 flex items-start space-x-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>临时账号安全吗？</span>
              </h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                我们提供的临时账号仅供下载使用，不会记录任何个人信息。下载完成后请使用自己的Apple ID，确保账号安全。
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="card"
            >
              <h4 className="font-semibold text-gray-900 mb-3 flex items-start space-x-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>连接速度如何？</span>
              </h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                我们提供3.5G超高速带宽，平均延迟低于50ms，支持4K视频流畅播放，游戏延迟极低。
              </p>
            </motion.div>

           
          </div>
        </motion.div>


      </div>
    </section>
  )
}

export default DownloadSection
